import {
  type EnvironmentId,
  type MessageId,
  type OrchestrationTurnInspection,
  type ServerProviderSkill,
  ThreadId,
  type TurnId,
} from "@t3tools/contracts";
import {
  createContext,
  memo,
  use,
  useEffect,
  Fragment,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { msg, t } from "@lingui/core/macro";
import { Plural, Trans, useLingui } from "@lingui/react/macro";
import type { I18n, MessageDescriptor } from "@lingui/core";
import { useTimelineScroll, type TimelineScrollHandle } from "./useTimelineScroll";
import { scopeThreadRef } from "@t3tools/client-runtime";
import {
  deriveChildTaskStatus,
  type TimelineEntry,
  deriveWorkLogRawFields,
  formatClock,
  formatDuration,
  formatElapsed,
  type WorkLogEntry,
} from "../../session-logic";
import { selectEnvironmentState, selectRunningSubAgentChildCount, useStore } from "../../store";
import { useInspectAccess } from "../settings/useInspectAccess";
import { openSubAgentList, openSubAgentPanelForEntry } from "../../subAgentPanelStore";
import { taskStatusLabel, type TaskDisplayStatus } from "./taskStatusLabels";
import { type ChatMessage } from "../../types";
import ChatMarkdown from "../ChatMarkdown";
import {
  BotIcon,
  ChevronRightIcon,
  DatabaseIcon,
  FilePenIcon,
  ImageIcon,
  type LucideIcon,
  PuzzleIcon,
  SearchIcon,
  ShrinkIcon,
  TerminalIcon,
  WrenchIcon,
} from "lucide-react";
import { Button } from "../ui/button";
import { type ExpandedImagePreview } from "./ExpandedImagePreview";
import { AttachmentsBlock } from "./AttachmentsBlock";
import { MessageCopyButton } from "./MessageCopyButton";
import { TurnInspectionButton } from "./TurnInspectionButton";
import {
  groupWorkLogEntries,
  computeStableMessagesTimelineRows,
  deriveMessagesTimelineRows,
  normalizeCompactToolLabel,
  rawPanelDetail,
  resolveAssistantMessageCopyState,
  type StableMessagesTimelineRowsState,
  type MessagesTimelineRow,
  type TurnGroupItem,
  type TurnStatus,
  type WorkLogGroup,
} from "./MessagesTimeline.logic";
import { LiveText } from "./LiveText";
import { Tooltip, TooltipPopup, TooltipTrigger } from "../ui/tooltip";
import { cn } from "~/lib/utils";

import { formatWorkspaceRelativePath } from "../../filePathDisplay";

// ---------------------------------------------------------------------------
// Context — shared state consumed by every row component via Context.
// Propagates through row memo boundaries for shared callbacks and
// non-row-scoped state. `nowIso` is intentionally excluded — self-ticking
// components (WorkingTimer, LiveElapsed) handle it.
// ---------------------------------------------------------------------------

interface TimelineRowSharedState {
  routeThreadKey: string;
  markdownCwd: string | undefined;
  markdownArtifactsRoute: string | undefined;
  resolvedTheme: "light" | "dark";
  workspaceRoot: string | undefined;
  skills: ReadonlyArray<Pick<ServerProviderSkill, "name" | "displayName">>;
  activeThreadEnvironmentId: EnvironmentId;
  timelineThreadId: ThreadId;
  onImageExpand: (preview: ExpandedImagePreview) => void;
  onLoadActivityPayload: (activityId: string) => Promise<unknown>;
  onLoadTurnInspection: (turnId: TurnId) => Promise<OrchestrationTurnInspection | undefined>;
  inspect: boolean;
}

interface TimelineRowActivityState {
  isWorking: boolean;
}

const TimelineRowCtx = createContext<TimelineRowSharedState>(null!);
const TimelineRowActivityCtx = createContext<TimelineRowActivityState>(null!);
const TIMELINE_LIST_HEADER = <div className="h-3 sm:h-4" />;
const TIMELINE_LIST_FOOTER = <div className="h-3 sm:h-4" />;

const EMPTY_TIMELINE_SKILLS: ReadonlyArray<Pick<ServerProviderSkill, "name" | "displayName">> = [];

// ---------------------------------------------------------------------------
// Props (public API)
// ---------------------------------------------------------------------------

interface MessagesTimelineProps {
  isWorking: boolean;
  workingLabel?: string | null;
  activeTurnInProgress: boolean;
  activeTurnId?: TurnId | null;
  activeTurnStartedAt: string | null;
  timelineRef?: React.RefObject<TimelineScrollHandle | null>;
  timelineEntries: TimelineEntry[];
  routeThreadKey: string;
  onImageExpand: (preview: ExpandedImagePreview) => void;
  activeThreadEnvironmentId: EnvironmentId;
  timelineThreadId: ThreadId;
  markdownCwd: string | undefined;
  markdownArtifactsRoute?: string | undefined;
  resolvedTheme: "light" | "dark";
  workspaceRoot: string | undefined;
  skills?: ReadonlyArray<Pick<ServerProviderSkill, "name" | "displayName">>;
  onIsAtEndChange: (isAtEnd: boolean) => void;
  hasMoreBefore: boolean;
  loadingOlder: boolean;
  onLoadOlder: () => void;
  onLoadActivityPayload: (activityId: string) => Promise<unknown>;
  onLoadTurnInspection: (turnId: TurnId) => Promise<OrchestrationTurnInspection | undefined>;
  emptyState?: ReactNode;
  footer?: ReactNode;
  /** The thread's snapshot has not arrived yet: the timeline shows the loading state instead of rows. */
  loading?: boolean;
  loadingDelayMs?: number;
}

// ---------------------------------------------------------------------------
// MessagesTimeline — list owner
// ---------------------------------------------------------------------------

export const MessagesTimeline = memo(function MessagesTimeline({
  isWorking,
  workingLabel = null,
  activeTurnInProgress,
  activeTurnId,
  activeTurnStartedAt,
  timelineRef,
  timelineEntries,
  routeThreadKey,
  onImageExpand,
  activeThreadEnvironmentId,
  timelineThreadId,
  markdownCwd,
  markdownArtifactsRoute,
  resolvedTheme,
  workspaceRoot,
  skills = EMPTY_TIMELINE_SKILLS,
  onIsAtEndChange,
  hasMoreBefore,
  loadingOlder,
  onLoadOlder,
  onLoadActivityPayload,
  onLoadTurnInspection,
  emptyState,
  footer = null,
  loading = false,
  loadingDelayMs,
}: MessagesTimelineProps) {
  const rawRows = useMemo(
    () =>
      deriveMessagesTimelineRows({
        timelineEntries,
        isWorking,
        workingLabel,
        activeTurnInProgress,
        activeTurnId: activeTurnId ?? null,
        activeTurnStartedAt,
      }),
    [
      timelineEntries,
      isWorking,
      workingLabel,
      activeTurnInProgress,
      activeTurnId,
      activeTurnStartedAt,
    ],
  );
  const rows = useStableRows(rawRows);
  const { hasAccess: inspect } = useInspectAccess();

  const { scrollerRef, contentRef, onScroll } = useTimelineScroll({
    handleRef: timelineRef,
    onIsAtEndChange,
    layoutKey: rows,
  });

  const sharedState = useMemo<TimelineRowSharedState>(
    () => ({
      routeThreadKey,
      markdownCwd,
      markdownArtifactsRoute,
      resolvedTheme,
      workspaceRoot,
      skills,
      activeThreadEnvironmentId,
      timelineThreadId,
      onImageExpand,
      onLoadActivityPayload,
      onLoadTurnInspection,
      inspect,
    }),
    [
      routeThreadKey,
      markdownCwd,
      markdownArtifactsRoute,
      resolvedTheme,
      workspaceRoot,
      skills,
      activeThreadEnvironmentId,
      timelineThreadId,
      onImageExpand,
      onLoadActivityPayload,
      onLoadTurnInspection,
      inspect,
    ],
  );
  const activityState = useMemo<TimelineRowActivityState>(() => ({ isWorking }), [isWorking]);

  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const onLoadOlderRef = useRef(onLoadOlder);
  onLoadOlderRef.current = onLoadOlder;

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasMoreBefore) {
      return;
    }
    const root = node.closest("[data-timeline-scroll]");
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          onLoadOlderRef.current();
        }
      },
      { root, rootMargin: "200px 0px 0px 0px" },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMoreBefore, loadingOlder]);

  const listHeader = (
    <div>
      {hasMoreBefore ? (
        <div
          ref={sentinelRef}
          data-timeline-older-sentinel="true"
          className="flex justify-center py-3 text-xs text-muted-foreground/50"
        >
          {loadingOlder ? <Trans>Loading earlier messages…</Trans> : null}
        </div>
      ) : null}
      {TIMELINE_LIST_HEADER}
    </div>
  );

  if (loading) {
    return <TimelineLoadingMessage delayMs={loadingDelayMs} />;
  }

  if (rows.length === 0 && !isWorking) {
    return (
      emptyState ?? (
        <TimelineEmptyMessage>
          <Trans>Send a message to start the conversation.</Trans>
        </TimelineEmptyMessage>
      )
    );
  }

  return (
    <TimelineRowCtx value={sharedState}>
      <TimelineRowActivityCtx value={activityState}>
        <div
          ref={scrollerRef}
          onScroll={onScroll}
          data-timeline-scroll="true"
          className="h-full overflow-y-auto overflow-x-hidden overscroll-y-contain px-3 [overflow-anchor:none] sm:px-5"
        >
          <div ref={contentRef} className="relative">
            {listHeader}
            {rows.map((row) => (
              <div
                key={row.id}
                className="mx-auto w-full min-w-0 max-w-3xl overflow-x-clip"
                data-timeline-root="true"
              >
                <TimelineRowContent row={row} />
              </div>
            ))}
            {footer}
            {TIMELINE_LIST_FOOTER}
          </div>
        </div>
      </TimelineRowActivityCtx>
    </TimelineRowCtx>
  );
});

export function TimelineEmptyMessage({ children }: { children: ReactNode }) {
  return (
    <div className="flex h-full items-center justify-center">
      <p className="text-sm text-muted-foreground/30">{children}</p>
    </div>
  );
}

const TIMELINE_LOADING_DELAY_MS = 150;

/** Shown once the snapshot has been pending for `delayMs`, so an instant load never flashes it. */
export function TimelineLoadingMessage({
  delayMs = TIMELINE_LOADING_DELAY_MS,
}: {
  delayMs?: number | undefined;
}) {
  const [visible, setVisible] = useState(delayMs === 0);
  useEffect(() => {
    if (delayMs === 0) return;
    const timer = window.setTimeout(() => setVisible(true), delayMs);
    return () => window.clearTimeout(timer);
  }, [delayMs]);
  return (
    <div className="flex h-full items-center justify-center" data-timeline-loading="true">
      {visible ? (
        <p className="thread-loading-pulse text-sm" role="status">
          <Trans>Loading chat</Trans>
        </p>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// TimelineRowContent — the actual row component
// ---------------------------------------------------------------------------

type TimelineWorkEntry = WorkLogEntry;
type TimelineRow = MessagesTimelineRow;
type TurnSectionItem = Extract<TurnGroupItem, { kind: "section" }>;

const TimelineRowContent = memo(function TimelineRowContent({ row }: { row: TimelineRow }) {
  return (
    <div
      className={cn(
        "pb-4",
        row.kind === "message" && row.message.role === "assistant" ? "group/assistant" : null,
      )}
      data-timeline-row-id={row.id}
      data-timeline-row-kind={row.kind}
      data-message-id={row.kind === "message" ? row.message.id : undefined}
      data-message-role={row.kind === "message" ? row.message.role : undefined}
    >
      {row.kind === "message" && row.message.role === "user" ? <UserTimelineRow row={row} /> : null}
      {row.kind === "message" && row.message.role === "assistant" ? (
        <AssistantTimelineRow row={row} />
      ) : null}
      {row.kind === "turn-group" ? <TurnGroupTimelineRow row={row} /> : null}
      {row.kind === "working" ? <WorkingTimelineRow row={row} /> : null}
    </div>
  );
});

function UserTimelineRow({ row }: { row: Extract<TimelineRow, { kind: "message" }> }) {
  const ctx = use(TimelineRowCtx);
  const userAttachments = row.message.attachments ?? [];

  return (
    <div className="flex flex-col items-end gap-2">
      <AttachmentsBlock
        attachments={userAttachments}
        variant="message"
        onExpandImage={ctx.onImageExpand}
        className="w-full"
      />
      {(row.message.text.trim().length > 0 || userAttachments.length === 0) && (
        <div className="group relative max-w-[80%] rounded-2xl rounded-br-sm border border-border bg-secondary px-4 py-3">
          <CollapsibleUserMessageBody
            text={row.message.text}
            skills={ctx.skills}
            markdownCwd={ctx.markdownCwd}
            footer={
              <div className="flex items-center gap-1.5 opacity-0 transition-opacity duration-200 focus-within:opacity-100 group-hover:opacity-100">
                {row.message.text && <MessageCopyButton text={row.message.text} />}
              </div>
            }
          />
        </div>
      )}
    </div>
  );
}

function AssistantTimelineRow({ row }: { row: Extract<TimelineRow, { kind: "message" }> }) {
  const { t } = useLingui();
  const ctx = use(TimelineRowCtx);
  const messageText = row.message.text || (row.message.streaming ? "" : t`(empty response)`);

  return (
    <div className="min-w-0 px-1 py-0.5">
      <ChatMarkdown
        text={messageText}
        cwd={ctx.markdownCwd}
        artifactsRoute={ctx.markdownArtifactsRoute}
        isStreaming={Boolean(row.message.streaming)}
        skills={ctx.skills}
      />
      <div className="mt-1.5 flex items-center">
        <AssistantMessageActions row={row} />
      </div>
    </div>
  );
}

/** Everything the agent produced before its final answer, split into stages
 *  and collapsed under the turn's elapsed time. Owns its expand state so
 *  toggling re-renders only this row. */
const TurnGroupTimelineRow = memo(function TurnGroupTimelineRow({
  row,
}: {
  row: Extract<TimelineRow, { kind: "turn-group" }>;
}) {
  const { t } = useLingui();
  const [expanded, setExpanded] = useState(false);
  const elapsed = row.endedAt ? formatElapsed(row.startedAt, row.endedAt) : null;
  const settledLabel = elapsed ? t`Worked for ${elapsed}` : t`Work log`;

  if (row.live) {
    return (
      <div data-turn-group="true" data-turn-group-expanded="true" data-turn-group-live="true">
        <TurnItemsFeed items={row.items} status={row.status} startedAt={row.startedAt} />
      </div>
    );
  }

  return (
    <div
      data-turn-group="true"
      data-turn-group-expanded={expanded ? "true" : "false"}
      data-turn-group-live="false"
    >
      <button
        type="button"
        aria-expanded={expanded}
        data-scroll-anchor-ignore
        onClick={() => setExpanded((value) => !value)}
        className="flex items-center gap-1 py-1 text-sm text-muted-foreground/80 transition-colors duration-150 hover:text-foreground"
      >
        <span>{settledLabel}</span>
        <ChevronRightIcon
          aria-hidden="true"
          className={cn("size-3.5 transition-transform duration-200", expanded && "rotate-90")}
        />
      </button>
      {expanded ? (
        <div className="mt-2">
          <TurnItemsFeed items={row.items} status={row.status} startedAt={row.startedAt} />
        </div>
      ) : null}
      <div className="mt-2 h-px bg-border" />
    </div>
  );
});

function TurnItemsFeed({
  items,
  status,
  startedAt,
}: {
  items: ReadonlyArray<TurnGroupItem>;
  status: TurnStatus | null;
  startedAt: string;
}) {
  const ctx = use(TimelineRowCtx);
  return (
    <div className="space-y-2" data-turn-group-items="true">
      {items.map((item) =>
        item.kind === "section" ? (
          <TurnSectionBlock key={item.id} item={item} />
        ) : item.kind === "question" ? (
          <TurnQuestionBlock key={item.id} message={item.message} />
        ) : (
          <div key={item.id} className="min-w-0 px-1 py-0.5">
            <ChatMarkdown
              text={item.message.text}
              cwd={ctx.markdownCwd}
              artifactsRoute={ctx.markdownArtifactsRoute}
              isStreaming={Boolean(item.message.streaming)}
              skills={ctx.skills}
            />
          </div>
        ),
      )}
      {status ? <TurnStatusRow status={status} startedAt={startedAt} /> : null}
    </div>
  );
}

/** A question the agent asked without pausing; it stays in the feed at its place. */
const TurnQuestionBlock = memo(function TurnQuestionBlock({ message }: { message: ChatMessage }) {
  const ctx = use(TimelineRowCtx);
  const { t } = useLingui();
  return (
    <div
      className="min-w-0 border-l-2 border-border py-0.5 pr-1 pl-3"
      data-turn-question="true"
      data-turn-question-id={message.id}
    >
      <div className="text-xs text-muted-foreground/80">{t`Question`}</div>
      <ChatMarkdown
        text={message.text}
        cwd={ctx.markdownCwd}
        artifactsRoute={ctx.markdownArtifactsRoute}
        isStreaming={Boolean(message.streaming)}
        skills={ctx.skills}
      />
    </div>
  );
});

const TurnSectionBlock = memo(function TurnSectionBlock({ item }: { item: TurnSectionItem }) {
  const ctx = use(TimelineRowCtx);
  const rows = useMemo(
    () => groupWorkLogEntries(item.entries, item.open),
    [item.entries, item.open],
  );
  return (
    <div
      data-turn-section="true"
      data-turn-section-expanded="true"
      data-turn-section-kind={item.message ? "commentary" : "synthetic"}
    >
      {item.message ? (
        <div className="min-w-0 px-1 py-0.5" data-turn-section-heading="full">
          <ChatMarkdown
            text={item.message.text}
            cwd={ctx.markdownCwd}
            artifactsRoute={ctx.markdownArtifactsRoute}
            isStreaming={Boolean(item.message.streaming)}
            skills={ctx.skills}
          />
        </div>
      ) : null}
      <div className="mt-1 space-y-0.5 pl-5" data-turn-section-entries="true">
        {rows.map((row) =>
          row.kind === "group" ? (
            <WorkEntryGroup key={`work-group:${row.id}`} group={row} />
          ) : (
            <WorkEntryRow key={`work-row:${row.entry.id}`} workEntry={row.entry} />
          ),
        )}
      </div>
    </div>
  );
});

/** The turn's running clock, always on while the turn is live; the state adds its own words. */
function TurnStatusRow({ status, startedAt }: { status: TurnStatus; startedAt: string }) {
  const { activeThreadEnvironmentId, timelineThreadId } = use(TimelineRowCtx);
  const threadRef = scopeThreadRef(activeThreadEnvironmentId, timelineThreadId);
  const runningAgents = useStore((state) => selectRunningSubAgentChildCount(state, threadRef));
  const elapsed = useElapsedClock(startedAt);
  const waiting =
    status.kind === "waiting_helpers" || (status.kind === "thinking" && runningAgents > 0);
  const kind: TurnStatus["kind"] = waiting ? "waiting_helpers" : status.kind;
  const waitingLabel = status.kind === "waiting_helpers" ? status.entries[0]?.label : null;
  const agentCount = runningAgents > 0 ? ` (${runningAgents})` : "";

  return (
    <div className="pl-5" data-turn-status={kind}>
      {status.kind === "acting" ? (
        <div className="space-y-0.5">
          {status.entries.map((workEntry) => (
            <WorkEntryRow key={`work-row:${workEntry.id}`} workEntry={workEntry} />
          ))}
        </div>
      ) : null}
      <div
        className="flex items-center gap-2 py-0.5 pt-1 text-sm text-muted-foreground/70"
        data-turn-clock="true"
      >
        <span className="tabular-nums" data-turn-status-elapsed="true">
          {elapsed}
        </span>
        {kind === "thinking" ? <span className="thinking-ellipsis">{t`Thinking`}</span> : null}
        {waiting ? (
          <button
            type="button"
            className="cursor-pointer underline-offset-2 hover:text-foreground hover:underline"
            onClick={() => openSubAgentList(threadRef)}
          >
            {waitingLabel ?? t`Waiting for helpers`}
            {agentCount}
          </button>
        ) : null}
      </div>
    </div>
  );
}

function useElapsedClock(startedAt: string): string {
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNowMs(Date.now()), 1_000);
    return () => clearInterval(id);
  }, []);
  const startedMs = Date.parse(startedAt);
  return formatClock(Number.isFinite(startedMs) ? nowMs - startedMs : 0);
}

function PulsingDots() {
  return (
    <span className="inline-flex items-center gap-[3px]">
      <span className="h-1 w-1 rounded-full bg-muted-foreground/30 animate-pulse" />
      <span className="h-1 w-1 rounded-full bg-muted-foreground/30 animate-pulse [animation-delay:200ms]" />
      <span className="h-1 w-1 rounded-full bg-muted-foreground/30 animate-pulse [animation-delay:400ms]" />
    </span>
  );
}

function AssistantMessageActions({ row }: { row: Extract<TimelineRow, { kind: "message" }> }) {
  const { inspect, onLoadTurnInspection } = use(TimelineRowCtx);
  const assistantCopyState = resolveAssistantMessageCopyState({
    text: row.message.text ?? null,
    showCopyButton: row.showAssistantCopyButton,
    streaming: row.assistantCopyStreaming,
  });

  if (!assistantCopyState.visible) {
    return null;
  }

  return (
    <div className="flex items-center gap-1 opacity-0 transition-opacity duration-200 group-hover/assistant:opacity-100 has-data-popup-open:opacity-100">
      <MessageCopyButton
        text={assistantCopyState.text ?? ""}
        size="icon-xs"
        variant="outline"
        className="border-border/50 bg-background/35 text-muted-foreground/45 shadow-none hover:border-border/70 hover:bg-background/55 hover:text-muted-foreground/70"
      />
      {inspect && row.message.turnId ? (
        <TurnInspectionButton turnId={row.message.turnId} onLoad={onLoadTurnInspection} />
      ) : null}
    </div>
  );
}

function WorkingTimelineRow({ row }: { row: Extract<TimelineRow, { kind: "working" }> }) {
  const { activeThreadEnvironmentId, timelineThreadId } = use(TimelineRowCtx);
  const threadRef = scopeThreadRef(activeThreadEnvironmentId, timelineThreadId);
  const runningAgents = useStore((state) => selectRunningSubAgentChildCount(state, threadRef));
  const segments: Array<{ key: string; content: ReactNode }> = [
    {
      key: "elapsed",
      content: row.createdAt ? (
        <Trans>
          Working for <WorkingTimer createdAt={row.createdAt} />
        </Trans>
      ) : (
        <Trans>Working...</Trans>
      ),
    },
  ];
  if (row.label) {
    segments.push({ key: "label", content: row.label });
  }
  if (runningAgents > 0) {
    segments.push({
      key: "agents",
      content: (
        <button
          type="button"
          className="cursor-pointer underline-offset-2 hover:text-foreground hover:underline"
          onClick={() => openSubAgentList(threadRef)}
        >
          <Plural value={runningAgents} one="# agent working" other="# agents working" />
        </button>
      ),
    });
  }

  return (
    <div className="py-0.5 pl-1.5">
      <div className="flex items-center gap-2 pt-1 text-sm text-muted-foreground/70">
        <PulsingDots />
        <span>
          {segments.map((segment, index) => (
            <Fragment key={segment.key}>
              {index > 0 ? " · " : null}
              {segment.content}
            </Fragment>
          ))}
        </span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Self-ticking labels — update their own text nodes so elapsed-time display
// does not create a React commit every second while a response is streaming.
// ---------------------------------------------------------------------------

/** Live "Working for Xs" label. */
function WorkingTimer({ createdAt }: { createdAt: string }) {
  return <LiveText compute={() => formatWorkingTimerNow(createdAt)} />;
}

// ---------------------------------------------------------------------------
// Extracted row sections — own their state / store subscriptions so changes
// re-render only the affected row, not the entire list.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Leaf components
// ---------------------------------------------------------------------------

const MAX_COLLAPSED_USER_MESSAGE_LINES = 8;
const MAX_COLLAPSED_USER_MESSAGE_LENGTH = 600;
const COLLAPSED_USER_MESSAGE_FADE_HEIGHT_REM = 1.75;
const COLLAPSED_USER_MESSAGE_FADE_MASK = `linear-gradient(to bottom, black calc(100% - ${COLLAPSED_USER_MESSAGE_FADE_HEIGHT_REM}rem), transparent)`;

function shouldCollapseUserMessage(text: string): boolean {
  if (text.trim().length === 0) {
    return false;
  }

  return (
    text.length > MAX_COLLAPSED_USER_MESSAGE_LENGTH ||
    text.split("\n").length > MAX_COLLAPSED_USER_MESSAGE_LINES
  );
}

const CollapsibleUserMessageBody = memo(function CollapsibleUserMessageBody(props: {
  text: string;
  skills: ReadonlyArray<Pick<ServerProviderSkill, "name" | "displayName">>;
  markdownCwd: string | undefined;
  footer?: ReactNode;
}) {
  const { t } = useLingui();
  const [expanded, setExpanded] = useState(false);
  const hasVisibleBody = props.text.trim().length > 0;
  const canCollapse = hasVisibleBody && shouldCollapseUserMessage(props.text);
  const isCollapsed = canCollapse && !expanded;

  return (
    <div>
      {hasVisibleBody ? (
        <div
          className={cn("relative", isCollapsed && "max-h-44 overflow-hidden")}
          data-user-message-body="true"
          data-user-message-collapsed={isCollapsed ? "true" : "false"}
          data-user-message-collapsible={canCollapse ? "true" : "false"}
          data-user-message-fade={isCollapsed ? "true" : "false"}
          style={
            isCollapsed
              ? {
                  WebkitMaskImage: COLLAPSED_USER_MESSAGE_FADE_MASK,
                  maskImage: COLLAPSED_USER_MESSAGE_FADE_MASK,
                }
              : undefined
          }
        >
          <UserMessageBody
            text={props.text}
            skills={props.skills}
            markdownCwd={props.markdownCwd}
          />
        </div>
      ) : null}
      {canCollapse || props.footer ? (
        <div
          className={cn(
            "mt-1.5 flex items-center gap-2",
            canCollapse && props.footer ? "justify-between" : "justify-end",
          )}
          data-user-message-footer="true"
        >
          {canCollapse ? (
            <Button
              type="button"
              size="xs"
              variant="ghost"
              aria-expanded={expanded}
              data-scroll-anchor-ignore
              onClick={() => setExpanded((value) => !value)}
              className="-ml-1 h-6 rounded-md px-1.5 text-xs text-muted-foreground/72 hover:bg-muted/55 hover:text-foreground/85"
            >
              {expanded ? t`Show less` : t`Show full message`}
            </Button>
          ) : null}
          {props.footer ? (
            <div className="ml-auto flex items-center gap-2">{props.footer}</div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
});

const UserMessageBody = memo(function UserMessageBody(props: {
  text: string;
  skills: ReadonlyArray<Pick<ServerProviderSkill, "name" | "displayName">>;
  markdownCwd: string | undefined;
}) {
  if (props.text.length === 0) {
    return null;
  }

  return (
    <ChatMarkdown
      text={props.text}
      cwd={props.markdownCwd}
      skills={props.skills}
      className="text-foreground"
      lineBreaks
    />
  );
});

// ---------------------------------------------------------------------------
// Structural sharing — reuse old row references when data hasn't changed
// so LegendList (and React) can skip re-rendering unchanged items.
// ---------------------------------------------------------------------------

/** Returns a structurally-shared copy of `rows`: for each row whose content
 *  hasn't changed since last call, the previous object reference is reused. */
function useStableRows(rows: MessagesTimelineRow[]): MessagesTimelineRow[] {
  const prevState = useRef<StableMessagesTimelineRowsState>({
    byId: new Map<string, MessagesTimelineRow>(),
    result: [],
  });

  return useMemo(() => {
    const nextState = computeStableMessagesTimelineRows(rows, prevState.current);
    prevState.current = nextState;
    return nextState.result;
  }, [rows]);
}

// ---------------------------------------------------------------------------
// Pure helpers
// ---------------------------------------------------------------------------

function formatWorkingTimer(startIso: string, endIso: string): string | null {
  const startedAtMs = Date.parse(startIso);
  const endedAtMs = Date.parse(endIso);
  if (!Number.isFinite(startedAtMs) || !Number.isFinite(endedAtMs)) {
    return null;
  }

  const elapsedSeconds = Math.max(0, Math.floor((endedAtMs - startedAtMs) / 1000));
  if (elapsedSeconds < 60) {
    const seconds = elapsedSeconds;
    return t`${seconds}s`;
  }

  const hours = Math.floor(elapsedSeconds / 3600);
  const minutes = Math.floor((elapsedSeconds % 3600) / 60);
  const seconds = elapsedSeconds % 60;

  if (hours > 0) {
    return minutes > 0 ? `${t`${hours}h`} ${t`${minutes}m`}` : t`${hours}h`;
  }

  return seconds > 0 ? `${t`${minutes}m`} ${t`${seconds}s`}` : t`${minutes}m`;
}

function formatWorkingTimerNow(startIso: string): string {
  const seconds = 0;
  return formatWorkingTimer(startIso, new Date().toISOString()) ?? t`${seconds}s`;
}

function workToneClass(tone: "thinking" | "tool" | "info" | "error"): string {
  return tone === "error" ? "text-rose-300/50 dark:text-rose-300/50" : "text-muted-foreground/70";
}

function workEntryPreview(
  workEntry: Pick<TimelineWorkEntry, "detail" | "command" | "changedFiles">,
  workspaceRoot: string | undefined,
) {
  if (workEntry.command) return workEntry.command;
  if (workEntry.detail) return workEntry.detail;
  if ((workEntry.changedFiles?.length ?? 0) === 0) return null;
  const [firstPath] = workEntry.changedFiles ?? [];
  if (!firstPath) return null;
  const displayPath = formatWorkspaceRelativePath(firstPath, workspaceRoot);
  return workEntry.changedFiles!.length === 1
    ? displayPath
    : `${displayPath} +${workEntry.changedFiles!.length - 1} more`;
}

function workEntryRawCommand(
  workEntry: Pick<TimelineWorkEntry, "command" | "rawCommand">,
): string | null {
  const rawCommand = workEntry.rawCommand?.trim();
  if (!rawCommand || !workEntry.command) {
    return null;
  }
  return rawCommand === workEntry.command.trim() ? null : rawCommand;
}

function capitalizePhrase(value: string): string {
  const trimmed = value.trim();
  if (trimmed.length === 0) {
    return value;
  }
  return `${trimmed.charAt(0).toUpperCase()}${trimmed.slice(1)}`;
}

const TOOL_ACTIVITY_LABELS: ReadonlyMap<string, MessageDescriptor> = new Map([
  ["ran command", msg`Ran command`],
  ["command run", msg`Command run`],
  ["file change", msg`File change`],
  ["changed files", msg`Changed files`],
  ["read file", msg`Read file`],
  ["searched files", msg`Searched files`],
  ["web search", msg`Web search`],
  ["image view", msg`Image view`],
  ["mcp tool call", msg`MCP tool call`],
  ["tool call", msg`Tool call`],
  ["subagent task", msg`Subagent task`],
  ["tool", msg`Tool`],
  ["reasoning", msg`Reasoning`],
  ["plan", msg`Plan`],
  ["error", msg`Error`],
  ["item", msg`Item`],
  ["command approval requested", msg`Command approval requested`],
  ["file-read approval requested", msg`File-read approval requested`],
  ["file-change approval requested", msg`File-change approval requested`],
  ["approval resolved", msg`Approval resolved`],
  ["runtime error", msg`Runtime error`],
  ["plan updated", msg`Plan updated`],
  ["user input requested", msg`User input requested`],
  ["user input submitted", msg`User input submitted`],
  ["reasoning update", msg`Reasoning update`],
  ["context compacted", msg`Context compacted`],
  ["compacting context", msg`Compacting context…`],
  ["context compaction failed", msg`Context compaction failed`],
  ["tool updated", msg`Tool updated`],
]);

function localizeToolActivityLabel(i18n: I18n, label: string): string {
  const descriptor = TOOL_ACTIVITY_LABELS.get(label.toLowerCase());
  return descriptor ? i18n._(descriptor) : label;
}

function toolWorkEntryHeading(workEntry: TimelineWorkEntry): string {
  if (!workEntry.toolTitle) {
    return capitalizePhrase(normalizeCompactToolLabel(workEntry.label));
  }
  return capitalizePhrase(normalizeCompactToolLabel(workEntry.toolTitle));
}

/**
 * The authoritative status of a sub-agent task row is the child thread's turn
 * state (level signal, synced via the shell stream); event-derived
 * `taskStatus` only fills in when the child thread is not in the store
 * (archived or pre-migration histories).
 */
function useTaskDisplayStatus(workEntry: TimelineWorkEntry): TaskDisplayStatus | null {
  const { activeThreadEnvironmentId } = use(TimelineRowCtx);
  const childThreadId = workEntry.childThreadId;
  const childStatus = useStore((state) => {
    if (!childThreadId) {
      return null;
    }
    const environmentState = selectEnvironmentState(state, activeThreadEnvironmentId);
    const childId = ThreadId.make(childThreadId);
    return deriveChildTaskStatus(
      environmentState.threadTurnStateById[childId]?.latestTurn,
      environmentState.threadSessionById[childId],
    );
  });
  if (workEntry.taskId === undefined) {
    return null;
  }
  return childStatus ?? workEntry.taskStatus ?? null;
}

function taskDisplayTone(status: TaskDisplayStatus): TimelineWorkEntry["tone"] {
  if (status === "running" || status === "working") return "thinking";
  if (status === "failed") return "error";
  return "info";
}

const WORK_ENTRY_TYPE_ICONS: Record<NonNullable<TimelineWorkEntry["itemType"]>, LucideIcon> = {
  command_execution: TerminalIcon,
  file_change: FilePenIcon,
  image_view: ImageIcon,
  mcp_tool_call: DatabaseIcon,
  web_search: SearchIcon,
  collab_agent_tool_call: BotIcon,
  dynamic_tool_call: PuzzleIcon,
};

function WorkEntryTypeIcon({ itemType }: { itemType: NonNullable<TimelineWorkEntry["itemType"]> }) {
  const Icon = WORK_ENTRY_TYPE_ICONS[itemType];
  return (
    <Icon aria-hidden="true" className="size-3 opacity-70" data-work-entry-type-icon={itemType} />
  );
}

const WorkEntryRow = memo(function WorkEntryRow({ workEntry }: { workEntry: TimelineWorkEntry }) {
  const { i18n, t } = useLingui();
  const { activeThreadEnvironmentId, timelineThreadId, workspaceRoot, inspect } =
    use(TimelineRowCtx);
  const displayStatus = useTaskDisplayStatus(workEntry);
  const [rawOpen, setRawOpen] = useState(false);
  const tone = displayStatus ? taskDisplayTone(displayStatus) : workEntry.tone;
  const isPublic = workEntry.labelKey !== undefined;
  const baseHeading = isPublic
    ? workEntry.label
    : localizeToolActivityLabel(i18n, toolWorkEntryHeading(workEntry));
  const heading = displayStatus
    ? `${baseHeading} · ${taskStatusLabel(i18n, displayStatus)}`
    : baseHeading;
  const active = workEntry.active === true || workEntry.compactionPhase === "started";
  const iconType = workEntry.itemType ?? (displayStatus ? "collab_agent_tool_call" : undefined);

  return (
    <div
      data-work-entry="true"
      data-work-entry-id={workEntry.id}
      data-work-entry-active={active ? "true" : undefined}
      data-work-entry-audience={workEntry.audience}
      data-compaction-phase={workEntry.compactionPhase}
      className={cn(
        "rounded-lg px-1 py-1",
        (workEntry.childThreadId || inspect) && "cursor-pointer hover:bg-muted/40",
        active && "animate-pulse",
      )}
      onClick={
        workEntry.childThreadId
          ? () =>
              openSubAgentPanelForEntry(
                workEntry,
                scopeThreadRef(activeThreadEnvironmentId, timelineThreadId),
              )
          : inspect
            ? () => setRawOpen((value) => !value)
            : undefined
      }
    >
      <div className="flex items-center gap-2 transition-[opacity,translate] duration-200">
        <span
          className={cn(
            "flex size-5 shrink-0 items-center justify-center",
            tone === "error" ? "text-rose-400" : "text-muted-foreground",
          )}
        >
          {workEntry.compactionPhase ? (
            <ShrinkIcon
              aria-hidden="true"
              className="size-3 opacity-70"
              data-work-entry-compaction-icon="true"
            />
          ) : workEntry.audience === "developer" ? (
            <WrenchIcon
              aria-hidden="true"
              className="size-3 opacity-70"
              data-work-entry-developer-icon="true"
            />
          ) : iconType !== undefined ? (
            <WorkEntryTypeIcon itemType={iconType} />
          ) : (
            <span
              aria-hidden="true"
              className="size-1 rounded-full bg-current opacity-40"
              data-work-entry-marker="true"
            />
          )}
        </span>
        <div className="min-w-0 overflow-hidden">
          {isPublic ? (
            <p className={cn("truncate text-sm leading-6", workToneClass(tone))}>
              <span className={cn("text-foreground/80", workToneClass(tone))}>{heading}</span>
              {inspect && workEntry.unmatched ? (
                <span
                  className="ml-1.5 rounded-sm border border-border/60 px-1 text-[9px] uppercase tracking-wide text-muted-foreground/60"
                  data-work-entry-unmatched="true"
                >
                  {t`no rule`}
                </span>
              ) : null}
            </p>
          ) : (
            <LegacyWorkEntryLabel workEntry={workEntry} heading={heading} tone={tone} />
          )}
        </div>
        {inspect ? (
          <button
            type="button"
            aria-expanded={rawOpen}
            aria-label={rawOpen ? t`Hide raw details` : t`Show raw details`}
            data-work-entry-raw-toggle="true"
            data-scroll-anchor-ignore
            onClick={(event) => {
              event.stopPropagation();
              setRawOpen((value) => !value);
            }}
            className="flex size-5 shrink-0 items-center justify-center text-muted-foreground/50 transition-colors duration-150 hover:text-foreground"
          >
            <ChevronRightIcon
              aria-hidden="true"
              className={cn("size-3 transition-transform duration-200", rawOpen && "rotate-90")}
            />
          </button>
        ) : null}
      </div>
      {!isPublic ? (
        <LegacyChangedFiles workEntry={workEntry} workspaceRoot={workspaceRoot} />
      ) : null}
      {inspect && rawOpen ? <WorkEntryRawPanel workEntry={workEntry} /> : null}
    </div>
  );
});

const WorkEntryGroup = memo(function WorkEntryGroup({ group }: { group: WorkLogGroup }) {
  const { t } = useLingui();
  const [expanded, setExpanded] = useState(false);
  const more = group.entries.length - 1;
  const head = group.entries[0];
  const tone = head?.tone ?? "tool";

  return (
    <div
      data-work-entry-group="true"
      data-work-entry-group-id={group.id}
      data-work-entry-group-count={group.entries.length}
      data-work-entry-group-expanded={expanded ? "true" : "false"}
    >
      <button
        type="button"
        aria-expanded={expanded}
        data-work-entry-group-toggle="true"
        data-scroll-anchor-ignore
        onClick={() => setExpanded((value) => !value)}
        className="group flex max-w-full items-center gap-2 rounded-lg px-1 py-1 text-left text-sm leading-6"
      >
        <span className="flex size-5 shrink-0 items-center justify-center text-muted-foreground">
          {head?.audience === "developer" ? (
            <WrenchIcon
              aria-hidden="true"
              className="size-3 opacity-70"
              data-work-entry-developer-icon="true"
            />
          ) : head?.itemType !== undefined ? (
            <WorkEntryTypeIcon itemType={head.itemType} />
          ) : (
            <span
              aria-hidden="true"
              className="size-1 rounded-full bg-current opacity-40"
              data-work-entry-marker="true"
            />
          )}
        </span>
        <span
          className={cn(
            "truncate text-foreground/80 transition-colors duration-150 group-hover:text-foreground",
            workToneClass(tone),
          )}
        >
          {group.label}
        </span>
        <span className="shrink-0 text-muted-foreground/55" data-work-entry-group-more="true">
          {t`and ${more} more`}
        </span>
        <ChevronRightIcon
          aria-hidden="true"
          className={cn(
            "size-3.5 shrink-0 text-muted-foreground/50 transition-transform duration-200",
            expanded && "rotate-90",
          )}
        />
      </button>
      {expanded ? (
        <div className="space-y-0.5 pl-5" data-work-entry-group-entries="true">
          {group.entries.map((workEntry) => (
            <WorkEntryRow key={`work-row:${workEntry.id}`} workEntry={workEntry} />
          ))}
        </div>
      ) : null}
    </div>
  );
});

function LegacyWorkEntryLabel({
  workEntry,
  heading,
  tone,
}: {
  workEntry: TimelineWorkEntry;
  heading: string;
  tone: TimelineWorkEntry["tone"];
}) {
  const { workspaceRoot } = use(TimelineRowCtx);
  const rawPreview = workEntryPreview(workEntry, workspaceRoot);
  const preview =
    rawPreview &&
    normalizeCompactToolLabel(rawPreview).toLowerCase() ===
      normalizeCompactToolLabel(heading).toLowerCase()
      ? null
      : rawPreview;
  const rawCommand = workEntryRawCommand(workEntry);
  const displayText = preview ? `${heading} - ${preview}` : heading;

  if (rawCommand) {
    return (
      <div className="max-w-full">
        <Tooltip>
          <TooltipTrigger
            closeDelay={0}
            delay={75}
            render={
              <p
                className={cn(
                  "truncate rounded-sm text-xs leading-5",
                  workToneClass(tone),
                  preview ? "text-muted-foreground/70" : "",
                )}
                aria-label={displayText}
              />
            }
          >
            <span className={cn("text-foreground/80", workToneClass(tone))}>{heading}</span>
            {preview && <span className="text-muted-foreground/55"> - {preview}</span>}
          </TooltipTrigger>
          <TooltipPopup
            align="start"
            className="max-w-[min(56rem,calc(100vw-2rem))] px-0 py-0"
            side="top"
          >
            <div className="max-w-[min(56rem,calc(100vw-2rem))] overflow-x-auto px-1.5 py-1 font-mono text-[11px] leading-4 whitespace-nowrap">
              {rawCommand}
            </div>
          </TooltipPopup>
        </Tooltip>
      </div>
    );
  }

  return (
    <Tooltip>
      <TooltipTrigger className="block min-w-0 w-full text-left" aria-label={displayText}>
        <p
          className={cn(
            "truncate text-sm leading-6",
            workToneClass(tone),
            preview ? "text-muted-foreground/70" : "",
          )}
        >
          <span className={cn("text-foreground/80", workToneClass(tone))}>{heading}</span>
          {preview && <span className="text-muted-foreground/55"> - {preview}</span>}
        </p>
      </TooltipTrigger>
      <TooltipPopup className="max-w-[min(720px,calc(100vw-2rem))]">
        <p className="whitespace-pre-wrap wrap-break-word text-xs leading-5">{displayText}</p>
      </TooltipPopup>
    </Tooltip>
  );
}

function LegacyChangedFiles({
  workEntry,
  workspaceRoot,
}: {
  workEntry: TimelineWorkEntry;
  workspaceRoot: string | undefined;
}) {
  const changedFiles = workEntry.changedFiles ?? [];
  const previewIsChangedFiles = changedFiles.length > 0 && !workEntry.command && !workEntry.detail;
  if (changedFiles.length === 0 || previewIsChangedFiles) {
    return null;
  }
  return (
    <div className="mt-1 flex flex-wrap gap-1 pl-6">
      {changedFiles.slice(0, 4).map((filePath) => {
        const displayPath = formatWorkspaceRelativePath(filePath, workspaceRoot);
        return (
          <Tooltip key={`${workEntry.id}:${filePath}`}>
            <TooltipTrigger
              render={
                <span
                  className="rounded-md border border-border/55 bg-background/75 px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground/75"
                  aria-label={displayPath}
                />
              }
            >
              {displayPath}
            </TooltipTrigger>
            <TooltipPopup side="top" className="max-w-[min(40rem,calc(100vw-2rem))]">
              <span className="font-mono text-[11px] whitespace-nowrap">{displayPath}</span>
            </TooltipPopup>
          </Tooltip>
        );
      })}
      {changedFiles.length > 4 && (
        <span className="px-1 text-[10px] text-muted-foreground/55">
          +{changedFiles.length - 4}
        </span>
      )}
    </div>
  );
}

/** Developer view of one action: the raw command, tool arguments and paths
 *  behind the public label. A payload trimmed from the stream is fetched on
 *  demand and kept here, in the row, for as long as the panel is mounted. */
const ITEM_TYPE_LABELS: Record<NonNullable<TimelineWorkEntry["itemType"]>, MessageDescriptor> = {
  command_execution: msg`Shell command`,
  mcp_tool_call: msg`MCP tool`,
  image_view: msg`Image view`,
  file_change: msg`File change`,
  web_search: msg`Web search`,
  collab_agent_tool_call: msg`Helper agent`,
  dynamic_tool_call: msg`Dynamic tool`,
};

function WorkEntryRawPanel({ workEntry }: { workEntry: TimelineWorkEntry }) {
  const { t } = useLingui();
  const { onLoadActivityPayload } = use(TimelineRowCtx);
  const [loadedPayload, setLoadedPayload] = useState<unknown>(undefined);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const resolved: TimelineWorkEntry =
    loadedPayload === undefined
      ? workEntry
      : {
          ...workEntry,
          ...deriveWorkLogRawFields(loadedPayload, workEntry.label),
          payloadPending: false,
        };

  const loadPayload = () => {
    setLoading(true);
    setLoadError(null);
    onLoadActivityPayload(workEntry.id)
      .then((payload) => {
        setLoadedPayload(payload ?? null);
      })
      .catch((error: unknown) => {
        setLoadError(error instanceof Error ? error.message : String(error));
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <div
      className="mt-1 ml-7 space-y-2.5 rounded-md border border-border/50 bg-background/60 px-2.5 py-2 text-[11px] leading-4"
      data-work-entry-raw="true"
      onClick={(event) => event.stopPropagation()}
    >
      <RawSection title={t`Step`} id="meta">
        <WorkEntryRawMeta entry={resolved} />
      </RawSection>
      <WorkEntryRawInput entry={resolved} />
      <RawSection title={resolved.itemType === "file_change" ? t`Changes` : t`Output`} id="output">
        {resolved.output !== undefined ? (
          <pre
            className="max-h-60 overflow-auto whitespace-pre-wrap break-all font-mono text-[11px] leading-4 text-foreground/80"
            data-work-entry-output="true"
          >
            {resolved.output}
          </pre>
        ) : resolved.payloadPending ? (
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-muted-foreground/60">{t`Not loaded yet`}</span>
            <Button
              type="button"
              size="xs"
              variant="outline"
              disabled={loading}
              data-work-entry-load-payload="true"
              onClick={loadPayload}
            >
              {loading ? t`Loading…` : t`Load full payload`}
            </Button>
          </div>
        ) : (
          <span className="text-muted-foreground/50">{t`No output`}</span>
        )}
        {loadError ? <p className="text-rose-300/70">{loadError}</p> : null}
      </RawSection>
    </div>
  );
}

function RawSection({ title, id, children }: { title: string; id: string; children: ReactNode }) {
  return (
    <section className="space-y-1" data-work-entry-raw-section={id}>
      <h4 className="text-[10px] font-medium tracking-wide text-muted-foreground/50 uppercase">
        {title}
      </h4>
      {children}
    </section>
  );
}

function RawMetaRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt className="text-muted-foreground/55">{label}</dt>
      <dd className="min-w-0 break-all text-foreground/80">{children}</dd>
    </>
  );
}

function WorkEntryRawMeta({ entry }: { entry: TimelineWorkEntry }) {
  const { t, i18n } = useLingui();
  const typeLabel =
    entry.itemType !== undefined ? i18n._(ITEM_TYPE_LABELS[entry.itemType]) : entry.toolTitle;
  const mcpName = [entry.mcpServer, entry.mcpTool].filter((part) => part !== undefined).join(".");
  const exitCode = entry.exitCode;
  const status =
    exitCode !== undefined
      ? t`Exit code ${exitCode}`
      : entry.tone === "error"
        ? t`Error`
        : entry.taskStatus !== undefined
          ? taskStatusLabel(i18n, entry.taskStatus)
          : null;
  const startedAtMs = entry.startedAt !== undefined ? Date.parse(entry.startedAt) : Number.NaN;
  return (
    <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-0.5">
      {typeLabel !== undefined ? <RawMetaRow label={t`Type`}>{typeLabel}</RawMetaRow> : null}
      {entry.labelKey !== undefined ? (
        <RawMetaRow label={t`Rule`}>
          <code className="font-mono">{entry.labelKey}</code>
          {entry.labelParams !== undefined ? (
            <code className="ml-2 font-mono text-muted-foreground/70">
              {JSON.stringify(entry.labelParams)}
            </code>
          ) : null}
          {entry.audience !== undefined ? (
            <span className="ml-2 text-muted-foreground/70">{entry.audience}</span>
          ) : null}
          {entry.unmatched ? (
            <span className="ml-2 text-muted-foreground/70">{t`no rule`}</span>
          ) : null}
        </RawMetaRow>
      ) : null}
      {mcpName.length > 0 ? (
        <RawMetaRow label={t`Tool`}>
          <span className="font-mono" data-work-entry-mcp="true">
            {mcpName}
          </span>
        </RawMetaRow>
      ) : entry.lastToolName !== undefined ? (
        <RawMetaRow label={t`Tool`}>
          <span className="font-mono">{entry.lastToolName}</span>
        </RawMetaRow>
      ) : null}
      {status !== null ? <RawMetaRow label={t`Status`}>{status}</RawMetaRow> : null}
      {entry.durationMs !== undefined ? (
        <RawMetaRow label={t`Duration`}>{formatDuration(entry.durationMs)}</RawMetaRow>
      ) : null}
      {Number.isFinite(startedAtMs) ? (
        <RawMetaRow label={t`Started`}>
          {new Date(startedAtMs).toLocaleTimeString(i18n.locale)}
        </RawMetaRow>
      ) : null}
    </dl>
  );
}

function WorkEntryRawInput({ entry }: { entry: TimelineWorkEntry }) {
  const { t } = useLingui();
  const { workspaceRoot } = use(TimelineRowCtx);
  const command = entry.rawCommand ?? entry.command ?? null;
  const files = (entry.changedFiles ?? []).filter((filePath) => filePath !== entry.viewPath);
  const details = rawPanelDetail(entry);
  const mono = "whitespace-pre-wrap break-all font-mono text-[11px] leading-4 text-foreground/85";
  return (
    <>
      {command !== null ? (
        <RawSection title={t`Command`} id="command">
          <pre className={mono} data-work-entry-command="true">
            {command}
          </pre>
        </RawSection>
      ) : null}
      {entry.mcpArguments !== undefined ? (
        <RawSection title={t`Arguments`} id="arguments">
          <pre className={cn(mono, "max-h-40 overflow-auto")}>{entry.mcpArguments}</pre>
        </RawSection>
      ) : null}
      {entry.query !== undefined ? (
        <RawSection title={t`Query`} id="query">
          <p className={mono}>{entry.query}</p>
        </RawSection>
      ) : null}
      {entry.viewPath !== undefined ? (
        <RawSection title={t`File`} id="file">
          <p className={mono}>{formatWorkspaceRelativePath(entry.viewPath, workspaceRoot)}</p>
        </RawSection>
      ) : null}
      {files.length > 0 ? (
        <RawSection title={t`Files`} id="files">
          {files.map((filePath) => (
            <p key={filePath} className={mono}>
              {formatWorkspaceRelativePath(filePath, workspaceRoot)}
            </p>
          ))}
        </RawSection>
      ) : null}
      {details !== null ? (
        <RawSection title={t`Details`} id="details">
          <pre className={cn(mono, "max-h-40 overflow-auto text-muted-foreground/75")}>
            {details}
          </pre>
        </RawSection>
      ) : null}
    </>
  );
}
